import React from "react";
import { useT } from "@/lib/LanguageContext";
import useCrmMoney from "../hooks/useCrmMoney";
import DataTable from "../ui/DataTable";
import CrmButton from "../ui/CrmButton";
import StatusBadge from "../ui/StatusBadge";
import { PACK_CATALOG_STATUS_KEYS } from "../contract";
import { CRM } from "../../../constants/testIds/crm";

/**
 * NAME · SESSIONS · PRICE · ACTIVE CLIENTS · "Edit · Archive" (design §E.8). Archived
 * packs render the same row with an amber status pill and "Unarchive" in place of
 * "Archive" — never hidden, since a practice must be able to see and reactivate one.
 */
export default function PacksTable({ rows, canManage = false, onEdit, onArchive, onUnarchive }) {
  const t = useT("crm");
  const money = useCrmMoney();

  const columns = [
    {
      key: "name",
      header: t("packs.colName"),
      render: (r) => (
        <span className="flex items-center gap-2 font-medium">
          {r.name}
          {r.status === "archived" && <StatusBadge tone="warning">{t(PACK_CATALOG_STATUS_KEYS.archived)}</StatusBadge>}
        </span>
      ),
    },
    { key: "sessions", header: t("packs.colSessions"), render: (r) => r.session_count },
    { key: "price", header: t("packs.colPrice"), render: (r) => money(r.price_cents, r.currency) },
    { key: "active", header: t("packs.colActiveClients"), render: (r) => r.active_clients ?? 0 },
    ...(canManage ? [{
      key: "actions",
      header: t("packs.colActions"),
      className: "text-right",
      render: (r) => (
        <span className="flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
          <CrmButton variant="link" size="sm" onClick={() => onEdit(r)} data-testid={CRM.packsRowEdit(r.id)}>
            {t("packs.edit")}
          </CrmButton>
          {r.status === "archived" ? (
            <CrmButton variant="link" size="sm" onClick={() => onUnarchive(r)} data-testid={CRM.packsRowUnarchive(r.id)}>
              {t("packs.unarchive")}
            </CrmButton>
          ) : (
            <CrmButton variant="link" size="sm" onClick={() => onArchive(r)} data-testid={CRM.packsRowArchive(r.id)}>
              {t("packs.archive")}
            </CrmButton>
          )}
        </span>
      ),
    }] : []),
  ];

  return (
    <DataTable
      caption={t("packs.title")}
      columns={columns}
      rows={rows}
      ids={{ table: CRM.packsTable, row: CRM.packsRow }}
    />
  );
}
